/**
 * Inventory - HTML Overlay UI
 * Renders as DOM elements over the game canvas.
 * Same pattern as SkillEditorHTML.ts
 */

import { Player } from '@client/entities/Player';
import { EquipmentSlot, EquipmentItem, ItemMod, ItemRarity } from '@shared/types';
import { getItemColor } from '@shared/items/Item';
// Old currency system removed - using CurrencySystem from shared/crafting
import { applyCurrency, canApplyCurrency, CurrencyType } from '@shared/crafting/CurrencySystem';
import { getAvailableMods, collectModGroups } from '@shared/items/ModifierPool';
import { STAT_METADATA } from '@shared/modifiers/ModifierSystem';
import { t } from '@locales/index';

// ========================
// Constants
// ========================

const SLOT_ICONS: Record<EquipmentSlot, string> = {
  head: '\u{1FA96}', chest: '\u{1F6E1}', gloves: '\u{1F9E4}', boots: '\u{1F462}',
  belt: '\u26D3', weapon: '\u2694', offhand: '\u{1F6E1}', amulet: '\u{1F4FF}',
  ring1: '\u{1F48D}', ring2: '\u{1F48D}', cape: '\u{1F9E3}',
};

const SLOT_LABELS: Record<EquipmentSlot, string> = {
  head: 'Kask', chest: 'Zirh', gloves: 'Eldiven', boots: 'Cizme',
  belt: 'Kemer', weapon: 'Silah', offhand: 'Kalkan', amulet: 'Kolye',
  ring1: 'Yuzuk 1', ring2: 'Yuzuk 2', cape: 'Pelerin',
};

const DEFENSE_BADGES: Record<string, string> = {
  armour: 'AR', evasion: 'EV', energyShield: 'ES',
  armour_evasion: 'AR/EV', armour_energyShield: 'AR/ES', evasion_energyShield: 'EV/ES',
};

type TabName = 'items' | 'stats' | 'stones';

// ========================
// CSS Styles (injected once)
// ========================

const CSS = `
.inv-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  font-family: 'Segoe UI', Tahoma, sans-serif;
  color: #f4e8d4;
  user-select: none;
}

.inv-panel {
  background: linear-gradient(180deg, #1a1510, #0d0a08);
  border: 2px solid #3a3530;
  border-radius: 10px;
  width: 85vw;
  height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.inv-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #3a3530;
  flex-shrink: 0;
}

.inv-header h2 {
  color: #ffd700;
  font-size: 20px;
  margin: 0;
}

.inv-close {
  background: #3d3228;
  border: 1px solid #5a4a3a;
  color: #f4e8d4;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.inv-close:hover { background: #5a4a3a; }

.inv-body {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

/* Left: Equipment */
.inv-equip {
  width: 210px;
  min-width: 210px;
  border-right: 1px solid #3a3530;
  padding: 12px 10px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: radial-gradient(ellipse at center, #1e1a15 0%, #0d0a08 100%);
}

.inv-equip-title {
  color: #ffd700;
  font-size: 13px;
  font-weight: bold;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 0 0 8px rgba(255, 215, 0, 0.3);
}

/* Character paper-doll grid - 3 columns x 6 rows */
.inv-equip-body {
  position: relative;
  display: grid;
  grid-template-columns: 52px 52px 52px;
  grid-template-rows: repeat(6, auto);
  gap: 4px 6px;
  padding: 8px;
  background: radial-gradient(ellipse at 50% 35%, rgba(255,215,0,0.04) 0%, transparent 70%);
  border: 1px solid #2a2520;
  border-radius: 10px;
  justify-content: center;
}

/* Silhouette vertical line */
.inv-equip-body::before {
  content: '';
  position: absolute;
  top: 55px;
  bottom: 55px;
  left: 50%;
  width: 1px;
  background: linear-gradient(180deg, transparent, #2a2520 15%, #2a2520 85%, transparent);
  transform: translateX(-50%);
  z-index: 0;
}

/* Grid placement - 3 column layout */
.inv-equip-slot[data-slot="head"]    { grid-column: 2; grid-row: 1; }
.inv-equip-slot[data-slot="amulet"]  { grid-column: 1; grid-row: 2; }
.inv-equip-slot[data-slot="cape"]    { grid-column: 3; grid-row: 2; }
.inv-equip-slot[data-slot="weapon"]  { grid-column: 1; grid-row: 3; }
.inv-equip-slot[data-slot="chest"]   { grid-column: 2; grid-row: 3; }
.inv-equip-slot[data-slot="offhand"] { grid-column: 3; grid-row: 3; }
.inv-equip-slot[data-slot="gloves"]  { grid-column: 1; grid-row: 4; }
.inv-equip-slot[data-slot="belt"]    { grid-column: 2; grid-row: 4; }
.inv-equip-slot[data-slot="boots"]   { grid-column: 3; grid-row: 4; }
.inv-equip-slot[data-slot="ring1"]   { grid-column: 1; grid-row: 5; }
.inv-equip-slot[data-slot="ring2"]   { grid-column: 3; grid-row: 5; }

.inv-equip-slot {
  width: 48px;
  height: 56px;
  background: linear-gradient(180deg, #1c1916 0%, #121010 100%);
  border: 1.5px solid #2a2520;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  transition: all 0.15s;
  z-index: 1;
  gap: 1px;
  padding: 2px;
}

.inv-equip-slot:hover {
  background: linear-gradient(180deg, #2a2520 0%, #1a1815 100%);
  border-color: #5a4a3a;
  box-shadow: 0 0 6px rgba(255,215,0,0.12);
}

.inv-equip-slot.equipped {
  border-width: 2px;
  box-shadow: 0 0 8px var(--rarity-glow, rgba(255,255,255,0.1));
  background: linear-gradient(180deg, #22201a 0%, #161412 100%);
}

.inv-equip-slot .slot-icon {
  font-size: 18px;
  line-height: 1;
}

.inv-equip-slot .slot-label {
  font-size: 7px;
  color: #555;
  text-align: center;
  line-height: 1;
  white-space: nowrap;
}

.inv-equip-slot .item-name {
  font-size: 7px;
  max-width: 46px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  line-height: 1;
}

.inv-equip-slot .defense-badge {
  position: absolute;
  top: 1px;
  right: 2px;
  font-size: 6px;
  color: #88bbff;
  font-weight: bold;
}

.inv-equip-slot .level-badge {
  position: absolute;
  top: 1px;
  left: 2px;
  font-size: 6px;
  font-weight: bold;
  background: rgba(0,0,0,0.7);
  padding: 0 2px;
  border-radius: 2px;
}

/* Right panel */
.inv-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

.inv-tabs {
  display: flex;
  border-bottom: 1px solid #2a2520;
  flex-shrink: 0;
}

.inv-tab {
  padding: 8px 18px;
  cursor: pointer;
  color: #8a7e6e;
  font-size: 12px;
  border-bottom: 2px solid transparent;
  transition: all 0.15s;
}
.inv-tab:hover { color: #f4e8d4; }
.inv-tab.active { color: #ffd700; border-bottom-color: #ffd700; }

.inv-content {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* Item Grid */
.inv-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(44px, 1fr));
  gap: 3px;
}

.inv-cell {
  width: 44px;
  height: 44px;
  background: #151310;
  border: 1px dashed #2a2520;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  position: relative;
  transition: border-color 0.15s, background 0.15s;
}
.inv-cell:hover { background: #2a2520; }
.inv-cell.filled {
  border-style: solid;
  border-width: 2px;
}

.inv-cell .cell-level {
  position: absolute;
  bottom: 1px;
  right: 3px;
  font-size: 8px;
  color: #8a7e6e;
}

/* Gold bar */
.inv-gold {
  padding: 8px 10px;
  font-size: 13px;
  color: #ffd700;
  border-top: 1px solid #2a2520;
  flex-shrink: 0;
}

/* Inventory sub-layout: vertical tabs on left + grid on right */
.inv-items-layout {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.inv-bag-tabs {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px;
  border-right: 1px solid #2a2520;
  flex-shrink: 0;
  width: 36px;
  overflow-y: auto;
  overflow-x: hidden;
}
.inv-bag-tabs::-webkit-scrollbar { width: 3px; }
.inv-bag-tabs::-webkit-scrollbar-track { background: transparent; }
.inv-bag-tabs::-webkit-scrollbar-thumb { background: #3a3530; border-radius: 2px; }

.inv-bag-tab {
  width: 28px;
  height: 28px;
  background: #151310;
  border: 1px solid #2a2520;
  border-radius: 4px;
  color: #8a7e6e;
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}
.inv-bag-tab:hover { background: #2a2520; color: #f4e8d4; }
.inv-bag-tab.active { border-color: #ffd700; color: #ffd700; background: #2a2520; }
.inv-bag-tab.add-tab { color: #5a5; border-style: dashed; }
.inv-bag-tab.add-tab:hover { color: #8d8; border-color: #5a5; }

.inv-bag-content {
  flex: 1;
  overflow-y: auto;
  padding: 4px;
  min-height: 0;
}

/* Bottom panel: currency + future tabs */
.inv-bottom-panel {
  border-top: 1px solid #3a3530;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  height: 70px;
}

.inv-bottom-tabs {
  display: flex;
  border-bottom: 1px solid #2a2520;
  flex-shrink: 0;
}

.inv-bottom-tab {
  padding: 4px 12px;
  font-size: 10px;
  color: #8a7e6e;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}
.inv-bottom-tab:hover { color: #f4e8d4; }
.inv-bottom-tab.active { color: #ffd700; border-bottom-color: #ffd700; }

.inv-bottom-content {
  flex: 1;
  overflow-y: auto;
  padding: 6px;
}

.inv-currency-grid {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.inv-currency-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: #151310;
  border: 1px solid #2a2520;
  border-radius: 4px;
  font-size: 10px;
  color: #f4e8d4;
  cursor: pointer;
  transition: all 0.15s;
}
.inv-currency-item:hover { background: #2a2520; }
.inv-currency-item.selected {
  border-width: 2px;
  background: #2a2520;
  box-shadow: 0 0 8px var(--cur-color, #ffd700);
}

/* Craft mode banner */
.inv-craft-banner {
  background: linear-gradient(90deg, #2a1a10, #1a1510);
  border: 1px solid #ffd700;
  border-radius: 6px;
  padding: 6px 12px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  color: #ffd700;
  flex-shrink: 0;
}
.inv-craft-banner button {
  background: #3d2828;
  border: 1px solid #6a3a3a;
  color: #ff8888;
  padding: 2px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 10px;
}
.inv-craft-banner button:hover { background: #5a3030; }

/* Item cell in craft mode */
.inv-cell.craft-target {
  cursor: crosshair;
}
.inv-cell.craft-target:hover {
  box-shadow: 0 0 10px #ffd70066;
}

.inv-currency-count {
  color: #ffd700;
  font-weight: bold;
}

/* Stats tab */
.inv-stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.inv-stat-section h3 {
  color: #ffd700;
  font-size: 12px;
  margin: 0 0 6px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.inv-stat-row {
  display: flex;
  justify-content: space-between;
  padding: 3px 0;
  font-size: 12px;
  border-bottom: 1px solid #1a1815;
}
.inv-stat-label { color: #8a7e6e; }
.inv-stat-value { color: #f4e8d4; font-family: monospace; }

.inv-resist-bar {
  height: 6px;
  background: #1a1815;
  border-radius: 3px;
  margin-top: 2px;
  overflow: hidden;
}
.inv-resist-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.2s;
}

/* Stones tab */
.inv-stone-group {
  margin-bottom: 12px;
}
.inv-stone-group-header {
  font-size: 12px;
  font-weight: bold;
  padding: 4px 8px;
  border-radius: 4px;
  margin-bottom: 4px;
}
.inv-stone-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 4px;
}
.inv-stone-card {
  background: #151310;
  border: 1px solid #2a2520;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 11px;
}
.inv-stone-name {
  font-weight: bold;
  margin-bottom: 2px;
}
.inv-stone-effect {
  color: #8a7e6e;
  font-size: 10px;
}

/* Currency tab */
.inv-currency-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 6px;
}
.inv-currency-card {
  background: #151310;
  border: 1px solid #2a2520;
  border-radius: 6px;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.inv-currency-icon {
  font-size: 20px;
  width: 30px;
  text-align: center;
}
.inv-currency-info {
  flex: 1;
  min-width: 0;
}
.inv-currency-name {
  font-size: 11px;
  color: #f4e8d4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.inv-currency-count {
  font-size: 13px;
  font-family: monospace;
  font-weight: bold;
}

/* Tooltip */
.inv-tooltip {
  position: fixed;
  background: #1a1510;
  border: 1px solid #3a3530;
  border-radius: 6px;
  padding: 10px;
  max-width: 280px;
  z-index: 1100;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0,0,0,0.6);
}
.inv-tooltip-name {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 4px;
}
.inv-tooltip-meta {
  font-size: 10px;
  color: #8a7e6e;
  margin-bottom: 6px;
}
.inv-tooltip-divider {
  border: none;
  border-top: 1px solid #2a2520;
  margin: 4px 0;
}
.inv-tooltip-mod {
  font-size: 11px;
  padding: 1px 0;
}
.inv-tooltip-mod.implicit { color: #88bbff; }
.inv-tooltip-mod.prefix { color: #88dd88; }
.inv-tooltip-mod.suffix { color: #ddbb88; }
.inv-tooltip-tier {
  color: #5a4a3a;
  font-size: 9px;
  margin-left: 4px;
}

/* Footer */
.inv-footer {
  padding: 6px 16px;
  border-top: 1px solid #3a3530;
  font-size: 11px;
  color: #5a4a3a;
  text-align: center;
  flex-shrink: 0;
}

/* Scrollbar */
.inv-equip::-webkit-scrollbar,
.inv-content::-webkit-scrollbar {
  width: 6px;
}
.inv-equip::-webkit-scrollbar-track,
.inv-content::-webkit-scrollbar-track {
  background: #0d0a08;
}
.inv-equip::-webkit-scrollbar-thumb,
.inv-content::-webkit-scrollbar-thumb {
  background: #3a3530;
  border-radius: 3px;
}
`;

// ========================
// Inventory HTML UI
// ========================

export class InventoryHTML {
  private player: Player;
  private container: HTMLDivElement | null = null;
  private tooltip: HTMLDivElement | null = null;
  private _isOpen = false;
  private activeTab: TabName = 'items';
  private styleInjected = false;
  private inventoryTabs: string[] = ['Çanta 1'];  // User-created inventory tabs
  private activeInventoryTab = 0;                  // Currently selected inventory tab index
  private activeBottomTab = 0;                     // 0=currency, more later
  private selectedCurrency: string | null = null;  // Selected currency for crafting

  constructor(player: Player) {
    this.player = player;
  }

  open(): void {
    this._isOpen = true;
    this.render();
  }

  close(): void {
    this._isOpen = false;
    this.removeTooltip();
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }

  toggle(): void { this._isOpen ? this.close() : this.open(); }
  isVisible(): boolean { return this._isOpen; }

  // ---- Private ----

  private injectStyles(): void {
    if (this.styleInjected) return;
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);
    this.styleInjected = true;
  }

  private render(): void {
    if (!this._isOpen) return;
    this.injectStyles();
    if (this.container) this.container.remove();

    this.container = document.createElement('div');
    this.container.className = 'inv-overlay';
    this.container.addEventListener('mousedown', (e) => {
      if (e.target === this.container) this.close();
    });

    const panel = document.createElement('div');
    panel.className = 'inv-panel';

    panel.appendChild(this.buildHeader());

    const body = document.createElement('div');
    body.className = 'inv-body';
    body.appendChild(this.buildEquipmentPanel());
    body.appendChild(this.buildRightPanel());
    panel.appendChild(body);

    panel.appendChild(this.buildFooter());

    this.container.appendChild(panel);
    document.body.appendChild(this.container);
  }

  // ---- Header ----

  private buildHeader(): HTMLElement {
    const header = document.createElement('div');
    header.className = 'inv-header';

    const title = document.createElement('h2');
    title.textContent = 'Envanter';
    header.appendChild(title);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'inv-close';
    closeBtn.textContent = '\u2715';
    closeBtn.onclick = () => this.close();
    header.appendChild(closeBtn);

    return header;
  }

  // ---- Equipment Panel (left) ----

  private buildEquipmentPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'inv-equip';

    const title = document.createElement('div');
    title.className = 'inv-equip-title';
    title.textContent = 'Ekipman';
    panel.appendChild(title);

    const body = document.createElement('div');
    body.className = 'inv-equip-body';

    // All 11 slots - CSS Grid handles positioning via data-slot attribute
    const allSlots: EquipmentSlot[] = [
      'head', 'amulet', 'cape', 'weapon', 'chest', 'offhand',
      'belt', 'gloves', 'boots', 'ring1', 'ring2',
    ];

    for (const slot of allSlots) {
      body.appendChild(this.buildEquipSlot(slot));
    }

    panel.appendChild(body);
    return panel;
  }

  private buildEquipSlot(slot: EquipmentSlot): HTMLElement {
    const item = this.player.inventory.getEquippedItem(slot);
    const el = document.createElement('div');
    el.className = 'inv-equip-slot';
    el.setAttribute('data-slot', slot);

    if (item) {
      const color = getItemColor(item.rarity);
      el.classList.add('equipped');
      el.style.borderColor = color;
      el.style.setProperty('--rarity-glow', color + '40');

      // Icon
      const icon = document.createElement('span');
      icon.className = 'slot-icon';
      icon.textContent = SLOT_ICONS[slot];
      el.appendChild(icon);

      // Item name (truncated)
      const nameEl = document.createElement('span');
      nameEl.className = 'item-name';
      nameEl.style.color = color;
      nameEl.textContent = item.name || SLOT_LABELS[slot];
      el.appendChild(nameEl);

      // Defense badge
      if (item.defenseType) {
        const badge = document.createElement('span');
        badge.className = 'defense-badge';
        badge.textContent = DEFENSE_BADGES[item.defenseType] || '';
        el.appendChild(badge);
      }

      // Level badge
      if (item.level) {
        const lvl = document.createElement('span');
        lvl.className = 'level-badge';
        lvl.style.color = color;
        lvl.textContent = String(item.level);
        el.appendChild(lvl);
      }

      // Click to unequip
      el.onclick = () => {
        const unequipped = this.player.inventory.unequipItem(slot);
        if (unequipped) {
          this.player.inventory.addItem(unequipped);
          this.player.recalculateStats?.();
          this.render();
        }
      };

      // Tooltip
      el.addEventListener('mouseenter', (e) => this.showItemTooltip(item, e));
      el.addEventListener('mousemove', (e) => this.moveTooltip(e));
      el.addEventListener('mouseleave', () => this.removeTooltip());
    } else {
      el.style.borderStyle = 'dashed';
      const icon = document.createElement('span');
      icon.className = 'slot-icon';
      icon.textContent = SLOT_ICONS[slot];
      icon.style.opacity = '0.25';
      el.appendChild(icon);

      const label = document.createElement('span');
      label.className = 'slot-label';
      label.textContent = SLOT_LABELS[slot];
      el.appendChild(label);
    }

    return el;
  }

  // ---- Right Panel ----

  private buildRightPanel(): HTMLElement {
    const right = document.createElement('div');
    right.className = 'inv-right';

    // Tabs
    right.appendChild(this.buildTabs());

    // Content
    const content = document.createElement('div');
    content.className = 'inv-content';

    switch (this.activeTab) {
      case 'items': this.buildItemsTab(content); break;
      case 'stats': this.buildStatsTab(content); break;
      case 'stones': this.buildStonesTab(content); break;
    }

    right.appendChild(content);

    // Gold bar
    const gold = document.createElement('div');
    gold.className = 'inv-gold';
    gold.textContent = `\u{1F4B0} ${this.player.inventory.getGold().toLocaleString()} Gold`;
    right.appendChild(gold);

    return right;
  }

  private buildTabs(): HTMLElement {
    const tabs = document.createElement('div');
    tabs.className = 'inv-tabs';

    const tabDefs: { key: TabName; label: string }[] = [
      { key: 'items', label: 'Esyalar' },
      { key: 'stats', label: 'Istatistik' },
      { key: 'stones', label: 'Taslar' },
    ];

    for (const td of tabDefs) {
      const btn = document.createElement('div');
      btn.className = `inv-tab${this.activeTab === td.key ? ' active' : ''}`;
      btn.textContent = td.label;
      btn.onclick = () => { this.activeTab = td.key; this.render(); };
      tabs.appendChild(btn);
    }

    return tabs;
  }

  // ---- Items Tab ----

  private buildItemsTab(parent: HTMLElement): void {
    // Main layout: bag tabs (left) + item grid (right)
    const layout = document.createElement('div');
    layout.className = 'inv-items-layout';

    // Left: vertical bag tabs
    const tabBar = document.createElement('div');
    tabBar.className = 'inv-bag-tabs';

    for (let t = 0; t < this.inventoryTabs.length; t++) {
      const tab = document.createElement('div');
      tab.className = `inv-bag-tab${t === this.activeInventoryTab ? ' active' : ''}`;
      tab.textContent = `${t + 1}`;
      tab.title = this.inventoryTabs[t];
      tab.onclick = () => { this.activeInventoryTab = t; this.render(); };
      tabBar.appendChild(tab);
    }

    // Add tab button
    const addTab = document.createElement('div');
    addTab.className = 'inv-bag-tab add-tab';
    addTab.textContent = '+';
    addTab.title = 'Yeni sekme ekle';
    addTab.onclick = () => {
      this.inventoryTabs.push(`Çanta ${this.inventoryTabs.length + 1}`);
      this.activeInventoryTab = this.inventoryTabs.length - 1;
      this.render();
    };
    tabBar.appendChild(addTab);

    layout.appendChild(tabBar);

    // Right: item grid
    const bagContent = document.createElement('div');
    bagContent.className = 'inv-bag-content';

    // Craft mode banner
    if (this.selectedCurrency) {
      const curInfo = this.getCurrencyInfo(this.selectedCurrency);
      const banner = document.createElement('div');
      banner.className = 'inv-craft-banner';
      banner.innerHTML = `<span>${curInfo?.icon || ''} ${curInfo?.name || ''} seçili - bir eşyaya tıkla</span>`;
      const cancelBtn = document.createElement('button');
      cancelBtn.textContent = '✕ İptal';
      cancelBtn.onclick = () => { this.selectedCurrency = null; this.render(); };
      banner.appendChild(cancelBtn);
      bagContent.appendChild(banner);
    }

    const grid = document.createElement('div');
    grid.className = 'inv-grid';

    // Get items for active tab (fallback to all items if tab system not implemented in Inventory)
    const allItems = this.player.inventory.getAllItems();
    const items = allItems; // TODO: filter by tab when Inventory supports tabs

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const cell = document.createElement('div');
      cell.className = 'inv-cell';

      if (item && 'slot' in item) {
        const eq = item as EquipmentItem;
        cell.classList.add('filled');
        cell.style.borderColor = getItemColor(eq.rarity);
        cell.textContent = SLOT_ICONS[eq.slot] || '?';

        const lvl = document.createElement('span');
        lvl.className = 'cell-level';
        lvl.textContent = `${eq.level}`;
        cell.appendChild(lvl);

        // Craft mode: single click applies currency
        if (this.selectedCurrency) {
          const curKey = this.selectedCurrency as CurrencyType;
          const canApply = canApplyCurrency(curKey, eq);
          if (canApply) {
            cell.classList.add('craft-target');
          }
          cell.addEventListener('click', (clickEvent) => {
            if (!canApply) return;
            this.showCraftConfirmPopup(curKey, eq, i, items, clickEvent);
          });
        } else {
          // Normal mode: double-click to equip
          cell.addEventListener('dblclick', () => {
            const removed = this.player.inventory.removeItem(i);
            if (removed && 'slot' in removed) {
              const prev = this.player.inventory.equipItem(removed as EquipmentItem);
              if (prev) this.player.inventory.addItem(prev);
              this.player.recalculateStats?.();
              this.render();
            }
          });
        }

        cell.addEventListener('mouseenter', (e) => this.showItemTooltip(eq, e));
        cell.addEventListener('mousemove', (e) => this.moveTooltip(e));
        cell.addEventListener('mouseleave', () => this.removeTooltip());
      }

      grid.appendChild(cell);
    }

    // Fill empty slots to show grid shape
    const minSlots = 40;
    for (let i = items.length; i < minSlots; i++) {
      const cell = document.createElement('div');
      cell.className = 'inv-cell';
      grid.appendChild(cell);
    }

    bagContent.appendChild(grid);
    layout.appendChild(bagContent);
    parent.appendChild(layout);

    // Bottom panel: currency + future tabs
    const bottomPanel = document.createElement('div');
    bottomPanel.className = 'inv-bottom-panel';

    // Bottom tabs
    const bottomTabs = document.createElement('div');
    bottomTabs.className = 'inv-bottom-tabs';

    const bottomTabDefs = [
      { label: '🪙 Malzemeler', idx: 0 },
    ];

    for (const bt of bottomTabDefs) {
      const tab = document.createElement('div');
      tab.className = `inv-bottom-tab${this.activeBottomTab === bt.idx ? ' active' : ''}`;
      tab.textContent = bt.label;
      tab.onclick = () => { this.activeBottomTab = bt.idx; this.render(); };
      bottomTabs.appendChild(tab);
    }

    bottomPanel.appendChild(bottomTabs);

    // Bottom content
    const bottomContent = document.createElement('div');
    bottomContent.className = 'inv-bottom-content';

    if (this.activeBottomTab === 0) {
      this.buildCurrencyGrid(bottomContent);
    }

    bottomPanel.appendChild(bottomContent);
    parent.appendChild(bottomPanel);
  }

  private showCraftConfirmPopup(curKey: CurrencyType, item: EquipmentItem, itemIndex: number, items: any[], event: MouseEvent): void {
    // Remove existing popup
    document.querySelectorAll('.craft-confirm-popup').forEach(el => el.remove());

    const curInfo = this.getCurrencyInfo(curKey);
    if (!curInfo) return;

    const popup = document.createElement('div');
    popup.className = 'craft-confirm-popup';
    popup.style.cssText = `
      position:fixed;z-index:9999;
      left:${event.clientX + 10}px;top:${event.clientY - 200}px;
      background:linear-gradient(180deg, #1a1510, #0d0a08);
      border:2px solid ${curInfo.color};border-radius:10px;
      padding:14px 18px;min-width:280px;max-width:350px;
      box-shadow:0 8px 24px rgba(0,0,0,0.9);
      font-family:'Segoe UI',sans-serif;color:#f4e8d4;
    `;

    // Header
    const header = document.createElement('div');
    header.style.cssText = `font-weight:bold;font-size:14px;color:${curInfo.color};margin-bottom:10px;display:flex;align-items:center;gap:8px;`;
    header.textContent = `${curInfo.icon} ${curInfo.name}`;
    popup.appendChild(header);

    // Item info
    const itemColor = getItemColor(item.rarity);
    const itemInfo = document.createElement('div');
    itemInfo.style.cssText = `background:#151310;border:1px solid ${itemColor};border-radius:6px;padding:8px;margin-bottom:10px;`;
    const ilvl = item.ilvl ?? item.level;
    itemInfo.innerHTML = `<div style="color:${itemColor};font-weight:bold;font-size:12px">${item.name || 'Eşya'}</div>
      <div style="color:#888;font-size:10px">${item.rarity} - Seviye ${item.level} - iLvl ${ilvl}</div>`;
    popup.appendChild(itemInfo);

    // Current mods section
    const modsDiv = document.createElement('div');
    modsDiv.style.cssText = 'background:#111;border:1px solid #2a2520;border-radius:4px;padding:6px 8px;margin-bottom:8px;font-size:10px;max-height:120px;overflow-y:auto;';

    const prefixes = item.prefixes || [];
    const suffixes = item.suffixes || [];
    const implicits = item.implicit || [];

    if (implicits.length > 0) {
      modsDiv.innerHTML += `<div style="color:#888;font-size:9px;margin-bottom:2px">Implicit:</div>`;
      for (const m of implicits) modsDiv.innerHTML += `<div style="color:#8888cc;padding-left:6px">${this.formatMod(m)}</div>`;
    }
    if (prefixes.length > 0) {
      modsDiv.innerHTML += `<div style="color:#888;font-size:9px;margin-top:3px">Prefix (${prefixes.length}/3):</div>`;
      for (const m of prefixes) modsDiv.innerHTML += `<div style="color:#88bbff;padding-left:6px">${this.formatMod(m)}</div>`;
    } else {
      modsDiv.innerHTML += `<div style="color:#555;font-size:9px;margin-top:3px">Prefix: yok</div>`;
    }
    if (suffixes.length > 0) {
      modsDiv.innerHTML += `<div style="color:#888;font-size:9px;margin-top:3px">Suffix (${suffixes.length}/3):</div>`;
      for (const m of suffixes) modsDiv.innerHTML += `<div style="color:#88ffbb;padding-left:6px">${this.formatMod(m)}</div>`;
    } else {
      modsDiv.innerHTML += `<div style="color:#555;font-size:9px;margin-top:3px">Suffix: yok</div>`;
    }
    popup.appendChild(modsDiv);

    // What will happen (short summary)
    const effectDiv = document.createElement('div');
    effectDiv.style.cssText = 'margin-bottom:10px;font-size:11px;line-height:1.6;';

    const modGroups = collectModGroups(item);
    let showModList = false; // Whether to show side popup with mod pool
    let sideTitle = '';
    let sideMods: { mod: { type: string; namePrefix?: string; nameSuffix?: string }; tier: { valueMin: number; valueMax: number } }[] = [];

    switch (curKey) {
      case 'ruh_tozu':
        effectDiv.innerHTML = `
          <div style="color:#ffd700;font-weight:bold;margin-bottom:4px">Normal → Sihirli</div>
          <div style="color:#88dd88">+ 1 rastgele prefix eklenecek</div>
          <div style="color:#88dd88">+ 1 rastgele suffix eklenecek</div>`;
        showModList = true;
        sideTitle = 'Gelebilecek Modlar';
        sideMods = [...getAvailableMods(item, 'prefix', modGroups), ...getAvailableMods(item, 'suffix', modGroups)];
        break;
      case 'tengri_ozu': {
        const emptyPre = Math.max(0, 3 - prefixes.length);
        const emptySuf = Math.max(0, 3 - suffixes.length);
        effectDiv.innerHTML = `
          <div style="color:#ffd700;font-weight:bold;margin-bottom:4px">Sihirli → Nadir</div>
          <div style="color:#88dd88">+ ${emptyPre} prefix eklenecek</div>
          <div style="color:#88dd88">+ ${emptySuf} suffix eklenecek</div>`;
        showModList = true;
        sideTitle = 'Gelebilecek Modlar';
        sideMods = [...getAvailableMods(item, 'prefix', modGroups), ...getAvailableMods(item, 'suffix', modGroups)];
        break;
      }
      case 'erlik_tasi':
        effectDiv.innerHTML = `
          <div style="color:#ffd700;font-weight:bold;margin-bottom:4px">Mod Yenileme (Reroll)</div>
          <div style="color:#ff8888">- Tüm prefix/suffix silinecek</div>
          <div style="color:#88dd88">+ Yeni rastgele modlar atılacak</div>`;
        showModList = true;
        sideTitle = 'Gelebilecek Modlar';
        sideMods = [...getAvailableMods(item, 'prefix', new Set()), ...getAvailableMods(item, 'suffix', new Set())];
        break;
      case 'arindirma_suyu':
        effectDiv.innerHTML = `
          <div style="color:#ffd700;font-weight:bold;margin-bottom:4px">Arındırma → Normal</div>
          <div style="color:#ff8888">- ${prefixes.length} prefix silinecek</div>
          <div style="color:#ff8888">- ${suffixes.length} suffix silinecek</div>
          <div style="color:#888">Implicit modlar korunur</div>`;
        break;
      case 'ruh_muhru':
        effectDiv.innerHTML = `
          <div style="color:#ffd700;font-weight:bold;margin-bottom:4px">+1 Modifier Ekle</div>
          <div style="color:#888">Prefix: ${prefixes.length}/3 | Suffix: ${suffixes.length}/3</div>
          <div style="color:#88dd88">Boş slota rastgele 1 mod eklenecek</div>`;
        showModList = true;
        sideTitle = 'Gelebilecek Modlar';
        if (prefixes.length < 3) sideMods.push(...getAvailableMods(item, 'prefix', modGroups));
        if (suffixes.length < 3) sideMods.push(...getAvailableMods(item, 'suffix', modGroups));
        break;
      case 'yikim_tozu':
        effectDiv.innerHTML = `
          <div style="color:#ffd700;font-weight:bold;margin-bottom:4px">Rastgele 1 Mod Sil</div>
          <div style="color:#ff8888">Prefix/suffix modlardan biri rastgele silinecek</div>`;
        break;
      case 'tamga_muhru':
        effectDiv.innerHTML = `
          <div style="color:#ffd700;font-weight:bold;margin-bottom:4px">Nadir → Set</div>
          <div style="color:#88dd88">+ Rastgele bir set atanacak</div>
          <div style="color:#88dd88">+ Tüm modlar korunacak</div>`;
        break;
      case 'tengri_duasi':
        effectDiv.innerHTML = `
          <div style="color:#ffd700;font-weight:bold;margin-bottom:4px">Implicit Yenileme</div>
          <div style="color:#88dd88">+ Implicit değerler ±%20 yeniden atılacak</div>
          <div style="color:#888">Prefix/suffix modlar değişmez</div>`;
        break;
    }

    popup.appendChild(effectDiv);

    // Side popup for available mod pool
    if (showModList && sideMods.length > 0) {
      const sidePopup = document.createElement('div');
      sidePopup.className = 'craft-confirm-popup';
      sidePopup.style.cssText = `
        position:fixed;z-index:9998;
        background:linear-gradient(180deg, #151310, #0a0908);
        border:1px solid #3a3530;border-radius:8px;
        padding:10px 14px;width:220px;max-height:400px;overflow-y:auto;
        box-shadow:0 4px 16px rgba(0,0,0,0.8);
        font-family:'Segoe UI',sans-serif;color:#f4e8d4;
      `;

      sidePopup.innerHTML = `<div style="color:#ffd700;font-weight:bold;font-size:11px;margin-bottom:6px;border-bottom:1px solid #3a3530;padding-bottom:4px">${sideTitle}</div>`;

      // Group by prefix/suffix
      const prefixMods = sideMods.filter(m => (m.mod as any).pool === 'prefix');
      const suffixMods = sideMods.filter(m => (m.mod as any).pool === 'suffix');

      if (prefixMods.length > 0) {
        sidePopup.innerHTML += `<div style="color:#88bbff;font-size:9px;font-weight:bold;margin-top:4px">Prefix:</div>`;
        for (const name of this.uniqueModNames(prefixMods)) {
          sidePopup.innerHTML += `<div style="color:#aaccff;font-size:10px;padding:1px 0 1px 6px">• ${name}</div>`;
        }
      }
      if (suffixMods.length > 0) {
        sidePopup.innerHTML += `<div style="color:#88ffbb;font-size:9px;font-weight:bold;margin-top:6px">Suffix:</div>`;
        for (const name of this.uniqueModNames(suffixMods)) {
          sidePopup.innerHTML += `<div style="color:#aaffcc;font-size:10px;padding:1px 0 1px 6px">• ${name}</div>`;
        }
      }

      document.body.appendChild(sidePopup);

      // Position side popup next to main popup after main is placed
      requestAnimationFrame(() => {
        const mainRect = popup.getBoundingClientRect();
        let sideLeft = mainRect.right + 8;
        let sideTop = mainRect.top;
        // If no room on right, put on left
        if (sideLeft + 230 > window.innerWidth) sideLeft = mainRect.left - 230;
        if (sideTop + 400 > window.innerHeight) sideTop = window.innerHeight - 410;
        if (sideTop < 10) sideTop = 10;
        sidePopup.style.left = `${sideLeft}px`;
        sidePopup.style.top = `${sideTop}px`;
      });

      // Remove side popup when main popup closes
      const origRemove = popup.remove.bind(popup);
      popup.remove = () => { sidePopup.remove(); origRemove(); };
    }

    // Buttons
    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex;gap:8px;justify-content:flex-end;';

    const cancelBtn = document.createElement('button');
    cancelBtn.style.cssText = 'padding:6px 16px;background:#2a2520;border:1px solid #3a3530;color:#888;border-radius:6px;cursor:pointer;font-size:11px;';
    cancelBtn.textContent = 'İptal';
    cancelBtn.onclick = () => popup.remove();
    btnRow.appendChild(cancelBtn);

    const confirmBtn = document.createElement('button');
    confirmBtn.style.cssText = `padding:6px 16px;background:${curInfo.color}22;border:1px solid ${curInfo.color};color:${curInfo.color};border-radius:6px;cursor:pointer;font-size:11px;font-weight:bold;`;
    confirmBtn.textContent = `${curInfo.icon} Uygula`;
    confirmBtn.onmouseenter = () => { confirmBtn.style.background = curInfo.color + '44'; };
    confirmBtn.onmouseleave = () => { confirmBtn.style.background = curInfo.color + '22'; };
    confirmBtn.onclick = () => {
      const result = applyCurrency(curKey, item);
      if (result.success && result.item) {
        items[itemIndex] = result.item;
        const curAmount = this.player.currencyStash.get(curKey) || 0;
        if (curAmount > 0) this.player.currencyStash.set(curKey, curAmount - 1);
        if ((this.player.currencyStash.get(curKey) || 0) <= 0) {
          this.selectedCurrency = null;
        }
      }
      popup.remove();
      this.render();
    };
    btnRow.appendChild(confirmBtn);

    popup.appendChild(btnRow);

    // Close on outside click
    const closeHandler = (ev: MouseEvent) => {
      if (!popup.contains(ev.target as Node)) {
        popup.remove();
        document.removeEventListener('mousedown', closeHandler);
      }
    };
    setTimeout(() => document.addEventListener('mousedown', closeHandler), 10);

    document.body.appendChild(popup);

    // Clamp position to screen
    const rect = popup.getBoundingClientRect();
    if (rect.bottom > window.innerHeight) popup.style.top = `${window.innerHeight - rect.height - 10}px`;
    if (rect.right > window.innerWidth) popup.style.left = `${window.innerWidth - rect.width - 10}px`;
    if (rect.top < 0) popup.style.top = '10px';
  }

  /** Format a mod using STAT_METADATA as single source of truth */
  private formatMod(mod: ItemMod): string {
    const sign = mod.value >= 0 ? '+' : '';
    const meta = (STAT_METADATA as Record<string, { nameTr?: string; isPercent?: boolean }>)[mod.type];
    const typeName = meta?.nameTr || mod.type;
    const pct = mod.calcType === 'increased' || mod.calcType === 'more' ? '%' : '';
    return `${sign}${mod.value}${pct} ${typeName}`;
  }

  /** List unique mods with full value range across all available tiers, using STAT_METADATA */
  private uniqueModNames(mods: { mod: { type: string; calcType?: string }; tier: { valueMin: number; valueMax: number; tier?: number } }[]): string[] {
    // Group all tiers per mod type, find overall min-max
    const grouped = new Map<string, { calcType?: string; minVal: number; maxVal: number; bestTier: number }>();
    for (const m of mods) {
      const existing = grouped.get(m.mod.type);
      if (existing) {
        existing.minVal = Math.min(existing.minVal, m.tier.valueMin);
        existing.maxVal = Math.max(existing.maxVal, m.tier.valueMax);
        existing.bestTier = Math.min(existing.bestTier, m.tier.tier || 8);
      } else {
        grouped.set(m.mod.type, {
          calcType: m.mod.calcType,
          minVal: m.tier.valueMin,
          maxVal: m.tier.valueMax,
          bestTier: m.tier.tier || 8,
        });
      }
    }

    const names: string[] = [];
    for (const [type, info] of grouped) {
      const meta = (STAT_METADATA as Record<string, { nameTr?: string }>)[type];
      const typeName = meta?.nameTr || type;
      const pct = info.calcType === 'increased' || info.calcType === 'more' ? '%' : '';
      names.push(`+${info.minVal}-${info.maxVal}${pct} ${typeName}`);
    }
    return names;
  }

  private getCurrencyInfo(key: string): { icon: string; name: string; desc: string; color: string } | null {
    const all: Record<string, { icon: string; name: string; desc: string; color: string }> = {
      ruh_tozu: { icon: '🌀', name: 'Ruh Tozu', desc: 'Normal → Sihirli', color: '#88aaff' },
      tengri_ozu: { icon: '✨', name: 'Tengri Özü', desc: 'Sihirli → Nadir', color: '#ffdd44' },
      erlik_tasi: { icon: '💀', name: 'Erlik Taşı', desc: 'Nadir modları yeniden at', color: '#cc44ff' },
      arindirma_suyu: { icon: '💧', name: 'Arındırma Suyu', desc: 'Tüm modları sil → Normal', color: '#44ddff' },
      ruh_muhru: { icon: '🔮', name: 'Ruh Mührü', desc: 'Nadir eşyaya +1 mod', color: '#ff8844' },
      yikim_tozu: { icon: '🌑', name: 'Yıkım Tozu', desc: 'Rastgele 1 mod sil', color: '#ff4444' },
      tamga_muhru: { icon: '🏷️', name: 'Tamga Mührü', desc: 'Nadir → Set', color: '#44ff88' },
      tengri_duasi: { icon: '🙏', name: 'Tengri Duası', desc: 'Efsanevi implicit reroll', color: '#ffd700' },
    };
    return all[key] || null;
  }

  private buildCurrencyGrid(parent: HTMLElement): void {
    const grid = document.createElement('div');
    grid.className = 'inv-currency-grid';

    const currencies: { key: string; icon: string; name: string; desc: string; color: string }[] = [
      { key: 'ruh_tozu', icon: '🌀', name: 'Ruh Tozu', desc: 'Normal → Sihirli (1 prefix + 1 suffix ekler)', color: '#88aaff' },
      { key: 'tengri_ozu', icon: '✨', name: 'Tengri Özü', desc: 'Sihirli → Nadir (eksik modları doldurur)', color: '#ffdd44' },
      { key: 'erlik_tasi', icon: '💀', name: 'Erlik Taşı', desc: 'Nadir eşyanın tüm modlarını yeniden atar', color: '#cc44ff' },
      { key: 'arindirma_suyu', icon: '💧', name: 'Arındırma Suyu', desc: 'Tüm modları siler, eşyayı Normal yapar', color: '#44ddff' },
      { key: 'ruh_muhru', icon: '🔮', name: 'Ruh Mührü', desc: 'Nadir eşyaya 1 modifier ekler (boş slot varsa)', color: '#ff8844' },
      { key: 'yikim_tozu', icon: '🌑', name: 'Yıkım Tozu', desc: 'Nadir eşyadan rastgele 1 modifier siler', color: '#ff4444' },
      { key: 'tamga_muhru', icon: '🏷️', name: 'Tamga Mührü', desc: 'Nadir → Set (rastgele set atanır, modlar korunur)', color: '#44ff88' },
      { key: 'tengri_duasi', icon: '🙏', name: 'Tengri Duası', desc: 'Efsanevi eşyanın implicit değerlerini yeniden atar', color: '#ffd700' },
    ];

    for (const cur of currencies) {
      const amount = this.player.currencyStash.get(cur.key) || 0;

      const item = document.createElement('div');
      item.className = 'inv-currency-item';
      item.style.borderColor = amount > 0 ? cur.color + '44' : '#2a2520';
      item.style.position = 'relative';

      const icon = document.createElement('span');
      icon.textContent = cur.icon;
      item.appendChild(icon);

      const name = document.createElement('span');
      name.textContent = cur.name;
      name.style.color = amount > 0 ? '#ccc' : '#555';
      name.style.fontSize = '9px';
      item.appendChild(name);

      const count = document.createElement('span');
      count.className = 'inv-currency-count';
      count.style.color = amount > 0 ? cur.color : '#555';
      count.textContent = String(amount);
      item.appendChild(count);

      // Click to select currency for crafting
      if (amount > 0) {
        item.style.cursor = 'pointer';
        item.onclick = () => {
          if (this.selectedCurrency === cur.key) {
            this.selectedCurrency = null; // Deselect
          } else {
            this.selectedCurrency = cur.key;
          }
          this.render();
        };
      }

      // Highlight if selected
      if (this.selectedCurrency === cur.key) {
        item.classList.add('selected');
        item.style.setProperty('--cur-color', cur.color);
        item.style.borderColor = cur.color;
      }

      // Tooltip on hover
      item.addEventListener('mouseenter', () => {
        this.removeTooltip();
        const tip = document.createElement('div');
        tip.className = 'inv-tooltip';
        tip.style.cssText = 'position:fixed;z-index:9999;background:#1a1510;border:1px solid ' + cur.color + ';border-radius:6px;padding:8px 12px;pointer-events:none;max-width:240px;box-shadow:0 4px 12px rgba(0,0,0,0.8);';
        tip.innerHTML = `
          <div style="font-weight:bold;color:${cur.color};margin-bottom:4px">${cur.icon} ${cur.name}</div>
          <div style="font-size:11px;color:#ccc;line-height:1.4">${cur.desc}</div>
          <div style="font-size:10px;color:#888;margin-top:4px">Miktar: <span style="color:${cur.color};font-weight:bold">${amount}</span></div>
        `;
        document.body.appendChild(tip);
        this.tooltip = tip;
      });
      item.addEventListener('mousemove', (e) => {
        if (this.tooltip) {
          this.tooltip.style.left = `${e.clientX + 12}px`;
          this.tooltip.style.top = `${e.clientY - 10}px`;
        }
      });
      item.addEventListener('mouseleave', () => this.removeTooltip());

      grid.appendChild(item);
    }

    parent.appendChild(grid);
  }

  // ---- Stats Tab ----

  private buildStatsTab(parent: HTMLElement): void {
    const grid = document.createElement('div');
    grid.className = 'inv-stats-grid';

    const p = this.player;
    const s = p.stats;

    // Left column
    const left = document.createElement('div');
    left.className = 'inv-stat-section';

    // Attributes
    this.addSectionHeader(left, 'Nitelikler');
    this.addStatRow(left, 'Guc', `${s.strength}`);
    this.addStatRow(left, 'Ceviklik', `${s.agility}`);
    this.addStatRow(left, 'Ruh', `${s.spirit}`);

    // Resources
    this.addSectionHeader(left, 'Kaynaklar');
    this.addStatRow(left, 'Can', `${Math.floor(s.hp)} / ${Math.floor(s.maxHp)}`);
    this.addStatRow(left, 'Mana', `${Math.floor(s.mana)} / ${Math.floor(s.maxMana)}`);
    if (s.maxEnergyShield > 0) {
      this.addStatRow(left, 'Enerji Kalkani', `${Math.floor(s.energyShield)} / ${Math.floor(s.maxEnergyShield)}`);
    }

    grid.appendChild(left);

    // Right column
    const right = document.createElement('div');
    right.className = 'inv-stat-section';

    // Offense
    this.addSectionHeader(right, 'Saldiri');
    this.addStatRow(right, 'Hasar', `${p.attackDamage}`);
    this.addStatRow(right, 'Saldiri Hizi', `${p.attackSpeed.toFixed(2)}/s`);
    this.addStatRow(right, 'Seviye', `${p.level}`);

    // Defense
    this.addSectionHeader(right, 'Savunma');
    this.addStatRow(right, 'Zirh', `${s.armor}`);
    this.addStatRow(right, 'Kacinma', `${s.evasion}`);
    if (s.blockChance && s.blockChance > 0) {
      this.addStatRow(right, 'Engelleme', `${s.blockChance}%`);
    }

    // Resistances
    this.addSectionHeader(right, 'Direncler');
    const resists: { key: string; label: string; color: string }[] = [
      { key: 'od', label: 'Ates', color: '#ff4444' },
      { key: 'su', label: 'Buz', color: '#4488ff' },
      { key: 'gok', label: 'Simsek', color: '#ffdd44' },
      { key: 'karanlik', label: 'Karanlik', color: '#aa44ff' },
    ];

    for (const r of resists) {
      const val = p.resistances[r.key as keyof typeof p.resistances] || 0;
      const row = document.createElement('div');
      row.style.marginBottom = '4px';

      const labelRow = document.createElement('div');
      labelRow.className = 'inv-stat-row';
      labelRow.style.borderBottom = 'none';

      const lbl = document.createElement('span');
      lbl.className = 'inv-stat-label';
      lbl.textContent = r.label;
      labelRow.appendChild(lbl);

      const valSpan = document.createElement('span');
      valSpan.className = 'inv-stat-value';
      valSpan.style.color = r.color;
      valSpan.textContent = `${val}%`;
      labelRow.appendChild(valSpan);

      row.appendChild(labelRow);

      const bar = document.createElement('div');
      bar.className = 'inv-resist-bar';
      const fill = document.createElement('div');
      fill.className = 'inv-resist-fill';
      fill.style.background = r.color;
      fill.style.width = `${Math.min(100, Math.max(0, (val / 90) * 100))}%`;
      bar.appendChild(fill);
      row.appendChild(bar);

      right.appendChild(row);
    }

    grid.appendChild(right);
    parent.appendChild(grid);
  }

  // ---- Stones Tab ----

  private buildStonesTab(parent: HTMLElement): void {
    const stones = this.player.inventory.getEffectStones();

    if (stones.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'color: #5a4a3a; text-align: center; padding: 40px; font-size: 14px;';
      msg.textContent = 'Henuz efekt tasiniz yok';
      parent.appendChild(msg);
      return;
    }

    // Group by element type
    const groups: Record<string, typeof stones> = {};
    for (const stone of stones) {
      const key = stone.type || 'other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(stone);
    }

    const elementColors: Record<string, string> = {
      od: '#ff4444', su: '#4488ff', gok: '#ffdd44', karanlik: '#aa44ff', other: '#888',
    };
    const elementNames: Record<string, string> = {
      od: 'Ates', su: 'Su', gok: 'Gok', karanlik: 'Karanlik', other: 'Diger',
    };

    for (const [key, group] of Object.entries(groups)) {
      const section = document.createElement('div');
      section.className = 'inv-stone-group';

      const header = document.createElement('div');
      header.className = 'inv-stone-group-header';
      header.style.background = (elementColors[key] || '#888') + '22';
      header.style.color = elementColors[key] || '#888';
      header.textContent = elementNames[key] || key;
      section.appendChild(header);

      const grid = document.createElement('div');
      grid.className = 'inv-stone-grid';

      for (const stone of group) {
        const card = document.createElement('div');
        card.className = 'inv-stone-card';

        const rarityColor = stone.rarity ? getItemColor(stone.rarity as ItemRarity) : '#a0a0a0';
        card.style.borderColor = rarityColor;

        const name = document.createElement('div');
        name.className = 'inv-stone-name';
        name.style.color = rarityColor;
        name.textContent = stone.name || stone.id;
        card.appendChild(name);

        // Show effect description if available
        if ('effect' in stone && stone.effect) {
          const eff = document.createElement('div');
          eff.className = 'inv-stone-effect';
          const desc = (stone.effect as { description?: string }).description;
          eff.textContent = desc || '';
          card.appendChild(eff);
        }

        grid.appendChild(card);
      }

      section.appendChild(grid);
      parent.appendChild(section);
    }
  }

  // ---- Currency Tab ----

  // ---- Footer ----

  private buildFooter(): HTMLElement {
    const footer = document.createElement('div');
    footer.className = 'inv-footer';
    footer.textContent = 'Cift tikla: Giy/Cikar  |  Ekipmana tikla: Cikar';
    return footer;
  }

  // ---- Helpers ----

  private addSectionHeader(parent: HTMLElement, text: string): void {
    const h = document.createElement('h3');
    h.textContent = text;
    parent.appendChild(h);
  }

  private addStatRow(parent: HTMLElement, label: string, value: string): void {
    const row = document.createElement('div');
    row.className = 'inv-stat-row';

    const lbl = document.createElement('span');
    lbl.className = 'inv-stat-label';
    lbl.textContent = label;
    row.appendChild(lbl);

    const val = document.createElement('span');
    val.className = 'inv-stat-value';
    val.textContent = value;
    row.appendChild(val);

    parent.appendChild(row);
  }

  // ---- Tooltip ----

  private showItemTooltip(item: EquipmentItem, e: MouseEvent): void {
    this.removeTooltip();

    const tip = document.createElement('div');
    tip.className = 'inv-tooltip';

    // Name
    const name = document.createElement('div');
    name.className = 'inv-tooltip-name';
    name.style.color = getItemColor(item.rarity);
    name.textContent = item.name;
    tip.appendChild(name);

    // Meta
    const meta = document.createElement('div');
    meta.className = 'inv-tooltip-meta';
    const slotName = SLOT_LABELS[item.slot] || item.slot;
    const ilvl = item.ilvl ?? item.level;
    meta.textContent = `${slotName} - ${item.rarity.charAt(0).toUpperCase() + item.rarity.slice(1)}`;
    tip.appendChild(meta);

    // Level + ilvl
    const lvlDiv = document.createElement('div');
    lvlDiv.style.cssText = 'font-size:10px;color:#888;margin-top:2px;';
    lvlDiv.textContent = `Seviye: ${item.level}  |  iLvl: ${ilvl}`;
    tip.appendChild(lvlDiv);

    // Implicit mods
    if (item.implicit && item.implicit.length > 0) {
      tip.appendChild(this.createDivider());
      for (const mod of item.implicit) {
        tip.appendChild(this.createModLine(mod, 'implicit'));
      }
    }

    // Prefix mods
    if (item.prefixes && item.prefixes.length > 0) {
      tip.appendChild(this.createDivider());
      for (const mod of item.prefixes) {
        tip.appendChild(this.createModLine(mod, 'prefix'));
      }
    }

    // Suffix mods
    if (item.suffixes && item.suffixes.length > 0) {
      tip.appendChild(this.createDivider());
      for (const mod of item.suffixes) {
        tip.appendChild(this.createModLine(mod, 'suffix'));
      }
    }

    this.tooltip = tip;
    document.body.appendChild(tip);
    this.moveTooltip(e);
  }

  private createDivider(): HTMLElement {
    const hr = document.createElement('hr');
    hr.className = 'inv-tooltip-divider';
    return hr;
  }

  private createModLine(mod: ItemMod, category: 'implicit' | 'prefix' | 'suffix'): HTMLElement {
    const div = document.createElement('div');
    div.className = `inv-tooltip-mod ${category}`;

    const sign = mod.value >= 0 ? '+' : '';
    const calcLabel = mod.calcType && mod.calcType !== 'flat' ? ` (${mod.calcType})` : '';
    const modName = this.getModDisplayName(mod.type);
    div.textContent = `${sign}${mod.value} ${modName}${calcLabel}`;

    const tier = document.createElement('span');
    tier.className = 'inv-tooltip-tier';
    tier.textContent = `T${mod.tier}`;
    div.appendChild(tier);

    return div;
  }

  private getModDisplayName(type: string): string {
    const key = `items.mods.${type}`;
    const result = t(key);
    if (result !== key) return result;
    // Fallback: split camelCase
    return type.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
  }

  private moveTooltip(e: MouseEvent): void {
    if (!this.tooltip) return;
    const x = Math.min(e.clientX + 14, window.innerWidth - 300);
    const y = Math.min(e.clientY + 14, window.innerHeight - 200);
    this.tooltip.style.left = `${x}px`;
    this.tooltip.style.top = `${y}px`;
  }

  private removeTooltip(): void {
    if (this.tooltip) {
      this.tooltip.remove();
      this.tooltip = null;
    }
  }
}
